import { ApiMetadata, ApiMetadataRetriever, ArrayUtil, ModelRow, StringUtil } from "@mcleod/core";
import { ComponentSearcher } from "./ComponentSearcher"
import { ComponentSearchResult } from "./ComponentSearchResult";
import { DropdownItem } from "../textbox/DropdownItem";
import { DropdownItemResolver } from "../textbox/DropdownItemResolver";
import { Textbox } from "../textbox/Textbox";
import { TableRow } from "./TableRow";
import { Table } from "./Table";

/**
 * Searcher class that facilitates the searching of values related to textboxes.
 *
 * Always search the textbox's field in the modelRow.  Conditionally include:
 * - The displayed value based on the label's defined format
 * - The lookup model display field
 * - The selected item's display value
 */
export class TextboxSearcher extends ComponentSearcher {
    private textboxDef: any;
    private dropdownItems: DropdownItem[];
    private lookupModelDisplayField: string;
    private lookupModelResultField: string;
    private table: Table;

    constructor(textboxDef: any, alias: string, table: Table) {
        super(textboxDef.field, alias);
        this.textboxDef = textboxDef;
        this.table = table;
    }

    protected addValuesToResult(searchResult: ComponentSearchResult, row: ModelRow) {
        this.addDisplayValue(searchResult, row, this.textboxDef.displayType, this.textboxDef.format);
        this.addLookupModelValues(searchResult, row);
        this.addDropdownItemValues(searchResult, row);
    }

    private addLookupModelValues(searchResult: ComponentSearchResult, row: ModelRow) {
        // If the textbox's def or metadata specifies lookup model display/result fields, look in those fields in the ModelRow
        const lmDataRows = row.getLookupModelData(this.fieldName)?.filter(data => data != null);
        if (ArrayUtil.isEmptyArray(lmDataRows))
            return;

        this.initLookupModelFields(row);

        for (const lmData of lmDataRows) {
            const displayValue = this.getDataValue(lmData, this.lookupModelDisplayField);
            const resultValue = this.getDataValue(lmData, this.lookupModelResultField);
            searchResult.addValue(displayValue);
            searchResult.addValue(resultValue);
        }
    }

    private getDataValue(data: any, field: string): any {
        if (!StringUtil.isEmptyString(field)) {
            return (data instanceof ModelRow) ? data.get(field) : data[field];
        }
    }

    private initLookupModelFields(row: ModelRow) {
        if (this.lookupModelDisplayField === undefined) {
            this.lookupModelDisplayField =
                this.textboxDef.lookupModelDisplayField ??
                this.getLookupModelApiMetadata(row)?.displayField ?? null;
        }

        if (this.lookupModelResultField === undefined) {
            this.lookupModelResultField =
                this.textboxDef.lookupModelResultField ??
                this.getLookupModelApiMetadata(row)?.keyFields?.[0] ?? null;
        }
    }

    private getLookupModelApiMetadata(modelRow: ModelRow): ApiMetadata {
        const lookupModel = this.getMetadataField(modelRow)?.lookupModel;
        if (lookupModel != null) {
            return ApiMetadataRetriever.getFromCache(lookupModel);
        }
    }

    private addDropdownItemValues(searchResult: ComponentSearchResult, row: ModelRow) {
        this.initDropdownItems(row);
        const dbValue = row.get(this.fieldName, null);
        const displayValue = this.dropdownItems.find(item => item.value === dbValue)?.displayValue;
        if (StringUtil.isEmptyString(displayValue) === false)
            searchResult.addValue(displayValue);
    }

    private initDropdownItems(row: ModelRow) {
        if (this.dropdownItems == null) {
            // Do our best to find the available dropdown items (which has limitations)
            if (this.textboxDef.items != null) {
                this.dropdownItems = DropdownItemResolver.createDropdownItems(this.textboxDef.items as []);
            } else if (this.getMetadataField(row)?.dbDisplayValues != null) {
                this.dropdownItems = DropdownItem.createFromValueDisplayValues(this.getMetadataField(row)?.dbDisplayValues);
            } else {
                // If we have a row already deserialized, see if the textbox in the first row of the table has items.  If it
                // does, use those.  This isn't perfect, and assumes that the textbox has the same items in every row, but
                // it at least has a chance of working.
                let textbox: Textbox;
                const firstRow = ArrayUtil.getFirstElement(this.table.allRows) as TableRow;
                if (firstRow != null)
                    textbox = firstRow.findComponentById(this.textboxDef.id) as Textbox;
                if (textbox?.items != null)
                    this.dropdownItems = DropdownItemResolver.resolveItems(textbox);
                else {
                    // We didn't find any items, populate with an empty array so we don't init items again
                    this.dropdownItems = [];
                }
            }
        }
    }
}
